import { AuthenticationModel } from '../models/Authentication'
import { boot } from 'quasar/wrappers'
import { useDatabase } from '../composables/database'
import { bootErrorMessage } from './error/bootErrorMessage'
import { Token_JWT } from '../types/components/data-rendering'

export default boot(async ({ app }: { app: any }) => {
  try {
    // Start up the SQLite Database
    const { loadDb, execute, query } = useDatabase()

    const createTables = `
      CREATE TABLE IF NOT EXISTS user (
        id INTEGER PRIMARY KEY CHECK (id = 0),
        users_data TEXT
      );

      CREATE TABLE IF NOT EXISTS timers (
        id INTEGER PRIMARY KEY NOT NULL,
        date TEXT,
        timer_data TEXT
      );


      CREATE TABLE IF NOT EXISTS timesheets (
        id INTEGER PRIMARY KEY NOT NULL,
        date TEXT,
        timesheets_data TEXT
      );

      CREATE TABLE IF NOT EXISTS cycle (
        id INTEGER PRIMARY KEY NOT NULL,
        current_cycle_data TEXT
      );

      CREATE TABLE IF NOT EXISTS payroll_window (
        id INTEGER PRIMARY KEY NOT NULL,
        current_payroll_data TEXT
      );

      CREATE TABLE IF NOT EXISTS stores (
        id INTEGER PRIMARY KEY CHECK (id = 0),
        last_updated TEXT,
        stores_data TEXT
      );

      CREATE TABLE IF NOT EXISTS pdfs (
        id INTEGER PRIMARY KEY NOT NULL,
        date TEXT,
        date_opened TEXT,
        pdf_path TEXT,
        pdf_url TEXT
      );

      CREATE TABLE IF NOT EXISTS task_data (
        id INTEGER PRIMARY KEY NOT NULL,
        task_data TEXT
      );

      CREATE TABLE IF NOT EXISTS expenses (
        id INTEGER PRIMARY KEY NOT NULL,
        expense_data TEXT NOT NULL
      );

      CREATE TABLE IF NOT EXISTS orders (
        id INTEGER PRIMARY KEY NOT NULL,
        orders_data TEXT NOT NULL
      );

      CREATE TABLE IF NOT EXISTS balances (
        id INTEGER PRIMARY KEY NOT NULL,
        balances_data TEXT NOT NULL
      );

      CREATE TABLE IF NOT EXISTS parts (
        id INTEGER PRIMARY KEY NOT NULL,
        parts_data TEXT NOT NULL
      );

      CREATE TABLE IF NOT EXISTS inventory_transactions (
        uuid STRING NOT NULL,
        tasks_id INTEGER,
        stores_id INTEGER,
        collections_id INTEGER,
        orders_id INTEGER,
        transaction_type STRING NOT NULL,
        from_entity INTEGER,
        to_entity INTEGER,
        part_number INTEGER,
        quantity INTEGER DEFAULT 0 NOT NULL,
        created_at STRING NOT NULL,
        reason STRING,
        reason_id INTEGER,
        comment STRING,
        successful_sync BOOLEAN DEFAULT FALSE
      );

      CREATE TABLE IF NOT EXISTS last_sync (
        id INTEGER PRIMARY KEY NOT NULL,
        last_synced TEXT
      );

      CREATE TABLE IF NOT EXISTS authentication_token (
        id INTEGER PRIMARY KEY CHECK (id = 0),
        token TEXT,
        staffing_users_id INTEGER,
        active BOOLEAN DEFAULT TRUE
      );

      CREATE TABLE IF NOT EXISTS recap_definitions (
        recap_id INTEGER PRIMARY KEY NOT NULL,
        recap_definition TEXT,
        task_ids TEXT
      );

      CREATE TABLE IF NOT EXISTS recap_responses (
        task_id INTEGER PRIMARY KEY NOT NULL,
        recap_id INTEGER,
        response TEXT,
        date_uploaded TEXT,
        is_complete BOOLEAN DEFAULT FALSE
      );

      CREATE TABLE IF NOT EXISTS recap_response_images (
        id INTEGER PRIMARY KEY NOT NULL,
        date_uploaded TEXT,
        recap_id INTEGER,
        task_id INTEGER,
        path TEXT NOT NULL,
        metadata TEXT
      );

      CREATE TABLE IF NOT EXISTS store_recap_definitions (
        recap_id INTEGER PRIMARY KEY NOT NULL,
        recap_definition TEXT,
        store_ids TEXT
      );

      CREATE TABLE IF NOT EXISTS store_recap_responses (
        id INTEGER PRIMARY KEY NOT NULL,
        store_id INTEGER,
        recap_id INTEGER,
        response TEXT,
        date_uploaded TEXT
      );

      CREATE TABLE IF NOT EXISTS store_recap_response_images (
        id INTEGER PRIMARY KEY NOT NULL,
        date_uploaded TEXT,
        recap_id INTEGER,
        store_id INTEGER,
        path TEXT NOT NULL,
        metadata TEXT
      );

      CREATE TABLE IF NOT EXISTS messages (
        id INTEGER PRIMARY KEY CHECK (id = 0),
        messages_data TEXT
      );

      CREATE TABLE IF NOT EXISTS codes (
        id INTEGER PRIMARY KEY CHECK (id = 0),
        codes_data TEXT
      );

      CREATE TABLE IF NOT EXISTS hashes (
        key STRING NOT NULL,
        hash TEXT
      );
    `

    try {
      await loadDb()
      await execute(createTables)
    } catch (err) {
      bootErrorMessage(err, { stage: 'database' })
    }

    // Update the timers table if necessary
    console.log(await query('select * from timers'))
    const checkTimersSQL = `
      PRAGMA table_info(timers);
    `
    const checkTimerRes = await query(checkTimersSQL)
    if (checkTimerRes) {
      const colNames = checkTimerRes.map((row: any) => row.name)
      if (!colNames.includes('attributes')) {
        console.log('migration: adding attributes col to timers table', {
          checkTimerRes,
        })
        const addCol = `
          ALTER TABLE timers ADD COLUMN attributes TEXT;
        `
        await execute(addCol)
      }
    }

    // Update the authentication table if necessary
    const checkAuthenticationSQL = `
      PRAGMA table_info(authentication_token);
    `
    const checkAuthRes = await query(checkAuthenticationSQL)
    if (checkAuthRes) {
      const colNames = checkAuthRes.map((row: any) => row.name)
      if (!colNames.includes('staffing_users_id')) {
        console.log('migration: adding staffing_users_id col to authentication_token table', {
          checkAuthRes,
        })
        //add staffing user id column
        const addCol = `
          ALTER TABLE authentication_token ADD COLUMN staffing_users_id INTEGER;
        `
        await execute(addCol)

        //migrate token user's id over
        const token = await AuthenticationModel().loadJWT()
        if (token) {
          const suid = JSON.parse(token.token) as Token_JWT
          const addSUID = `
            UPDATE authentication_token
            SET staffing_users_id = ${suid.staffing_users_id};
          `
          await execute(addSUID)
        }
      }
      if (!colNames.includes('active')) {
        console.log('migration: adding active col to authentication_token table', {
          checkAuthRes,
        })
        const addCol = `
          ALTER TABLE authentication_token ADD COLUMN active BOOLEAN DEFAULT TRUE;
        `
        await execute(addCol)
      }
    }

    // Update the recap response tables if necessary
    const checkRecapResponsesSQL = `
      PRAGMA index_list(store_recap_responses);
    `
    const responsesSqlResults = await query(checkRecapResponsesSQL)
    const addUniqueConstraint = `
      CREATE UNIQUE INDEX store_recap_responses_unique_index ON store_recap_responses(store_id, recap_id);
    `
    let shouldCreateStoreRecapIndex = true
    if (responsesSqlResults && responsesSqlResults.length) {
      const indexNames = responsesSqlResults.map((row: any) => row.name)
      for (const name of indexNames) {
        const indexInfo = await query(`PRAGMA index_info(${name});`)
        if (indexInfo && indexInfo.length) {
          const colNames = indexInfo.map((row: any) => row.name)
          if (colNames.includes('store_id') && colNames.includes('recap_id')) {
            shouldCreateStoreRecapIndex = false
          }
        }
      }
    }
    if (shouldCreateStoreRecapIndex) {
      await execute(addUniqueConstraint)
    }

    const checkRecapDefinitionHashSql = `
      PRAGMA table_info(recap_definitions);
    `
    const recapHashRes = await query(checkRecapDefinitionHashSql)
    if (recapHashRes) {
      const colNames = recapHashRes.map((row: any) => row.name)
      if (!colNames.includes('hash')) {
        console.log('migration: adding hash col to recap_definitions table', {
          recapHashRes,
        })
        const addCol = `
          ALTER TABLE recap_definitions ADD COLUMN hash TEXT;
        `
        await execute(addCol)
      }
    }
    const checkStoreRecapDefinitionHashSql = `
      PRAGMA table_info(store_recap_definitions);
    `
    const storeRecapHashRes = await query(checkStoreRecapDefinitionHashSql)
    if (storeRecapHashRes) {
      const colNames = storeRecapHashRes.map((row: any) => row.name)
      if (!colNames.includes('hash')) {
        console.log('migration: adding hash col to store_recap_definitions table', {
          storeRecapHashRes,
        })
        const addCol = `
          ALTER TABLE store_recap_definitions ADD COLUMN hash TEXT;
        `
        await execute(addCol)
      }
    }

    // Update the recap image tables if necessary
    const checkRecapResponseImagesSql = `
      PRAGMA table_info(recap_response_images);
    `
    const checkImageRes = await query(checkRecapResponseImagesSql)
    if (checkImageRes) {
      const colNames = checkImageRes.map((row: any) => row.name)
      if (!colNames.includes('field_instance_id')) {
        console.log('migration: adding field_instance_id col to recap_response_images table', {
          checkImageRes,
        })
        const addCol = `
          ALTER TABLE recap_response_images ADD COLUMN field_instance_id INTEGER;
        `
        await execute(addCol)
      }
      if (!colNames.includes('field_repeating_index')) {
        console.log('migration: adding field_repeating_index col to recap_response_images table', {
          checkImageRes,
        })
        const addCol = `
          ALTER TABLE recap_response_images ADD COLUMN field_repeating_index INTEGER;
        `
        await execute(addCol)
      }
    }
    const checkStoreRecapResponseImagesSql = `
      PRAGMA table_info(store_recap_response_images);
    `
    const storeCheckImageRes = await query(checkStoreRecapResponseImagesSql)
    if (storeCheckImageRes) {
      const colNames = storeCheckImageRes.map((row: any) => row.name)
      if (!colNames.includes('field_instance_id')) {
        console.log(
          'migration: adding field_instance_id col to store_recap_response_images table',
          {
            storeCheckImageRes,
          }
        )
        const addCol = `
          ALTER TABLE store_recap_response_images ADD COLUMN field_instance_id INTEGER;
        `
        await execute(addCol)
      }
      if (!colNames.includes('field_repeating_index')) {
        console.log(
          'migration: adding field_repeating_index col to store_recap_response_images table',
          {
            storeCheckImageRes,
          }
        )
        const addCol = `
          ALTER TABLE store_recap_response_images ADD COLUMN field_repeating_index INTEGER;
        `
        await execute(addCol)
      }
    }

    // Add a completed flag to recap response table if necessary
    const checkRecapResponseCompleteSql = `
      PRAGMA table_info(recap_responses);
    `
    const checkRecapResponseCompleteRes = await query(checkRecapResponseCompleteSql)
    if (checkRecapResponseCompleteRes) {
      const colNames = checkRecapResponseCompleteRes.map((row: any) => row.name)
      if (!colNames.includes('is_complete')) {
        console.log('migration: adding is_complete col to recap_responses table', {
          checkRecapResponseCompleteRes,
        })
        const addCol = `
          ALTER TABLE recap_responses ADD COLUMN is_complete BOOLEAN DEFAULT FALSE;
        `
        await execute(addCol)
      }
    }
  } catch (error) {
    bootErrorMessage(error, { stage: 'database' })
  }
})
